import { useCallback, useMemo } from 'react';
import type {
  CandidateRow,
  MappingReferenceType,
} from '@shared/models/element-import.model';
import type { ElementQuantityExpressionName } from '@shared/models/element-quantities.model';
import { useProductsLookup } from '@client/store/product';
import { useRecipeLookup } from '@client/store/recipe/recipe.hook';
import { useSelectedVersion } from '@client/store/ui';
import { useApplyProjectChanges } from '@client/store/project/use-apply-project-changes.hook';
import { useSnackbar } from '@client/hooks/use-snackbar.hooks';
import {
  groupImportedElementAdds,
  IMPORT_REPLACE_NEEDS_PROPOSAL_MESSAGE,
  placeRemappedImportAdds,
} from '@shared/helpers/import-replace.helpers';
import { selectedProposalIdForVersion } from './import-replace-target.helpers';
import { useImportedElements } from './candidate-status.helpers';
import type { ConversionFactorUnit } from '@shared/models/unit.model';
import type { IfcMeasure } from '@shared/helpers/ifc-quantity.helpers';
import {
  buildBulkConversionChanges,
  buildBulkIfcMeasureChanges,
  buildBulkMappingChanges,
  buildBulkQuantityNameChanges,
  buildBulkTotalQuantityChanges,
  bulkUpdatedMessage,
  type BulkRowActionResult,
  type BulkRowLookups,
} from './bulk-row-actions.helpers';
import type { MappingsByCode } from './element-imports.store';
import type { ProjectElementImport } from '@shared/models/element-import.model';
import { getVisibleListRowIds } from './list-selection.store';

export type BulkRowActions = {
  /**
   * `true` while an edit on `rowId` should fan out. The in-row handlers check
   * this and fall through to their single-row path when it is `false`, so the
   * existing edge cases stay on exactly the code that handles them today.
   */
  shouldFanOut: (rowId: string) => boolean;
  setMapping: (
    referenceId: string,
    referenceType: MappingReferenceType,
  ) => void;
  setQuantityName: (name: ElementQuantityExpressionName) => void;
  /** `sourceRowId` is the row typed into; the rest follow the rate it implies. */
  setTotalQuantity: (value: number, sourceRowId: string) => void;
  /** Conversion rate: the factor, the target unit, or both. */
  setConversionRate: (rate: {
    quantityFactor: number | undefined;
    unitTo?: ConversionFactorUnit;
  }) => void;
  setIfcMeasure: (measure: IfcMeasure) => void;
};

type UseBulkRowActionsArgs = {
  /** The selected rows, in the one import a selection can span. */
  rows: readonly CandidateRow[];
  selectedIds: ReadonlySet<string>;
  mappingsByCode: MappingsByCode;
  importsById: Record<string, ProjectElementImport | undefined>;
};

/**
 * Applies a bulk row edit as one batch (DEV-3456). Rows the edit cannot touch
 * are skipped silently per the ticket, and when it applies to none of them
 * nothing happens at all — no changes, no snackbar.
 */
export const useBulkRowActions = ({
  rows,
  selectedIds,
  mappingsByCode,
  importsById,
}: UseBulkRowActionsArgs): BulkRowActions => {
  const version = useSelectedVersion();
  const importedElements = useImportedElements(version);
  const productsById = useProductsLookup();
  const recipesById = useRecipeLookup();
  const applyChanges = useApplyProjectChanges();
  const { enqueueSnackbar } = useSnackbar();

  const selectedRows = useMemo(
    () => rows.filter((row) => selectedIds.has(row.id)),
    [rows, selectedIds],
  );

  const lookups: BulkRowLookups | undefined = useMemo(() => {
    if (!version) return undefined;
    return {
      version,
      mappingsByCode,
      productsById,
      recipesById,
      importsById,
      importedElements,
    };
  }, [
    version,
    mappingsByCode,
    productsById,
    recipesById,
    importsById,
    importedElements,
  ]);

  const apply = useCallback(
    (result: BulkRowActionResult) => {
      if (!version) return;
      if (result.appliedRowIds.length === 0 || result.changes.length === 0) {
        return;
      }
      const proposalId = selectedProposalIdForVersion(version);
      const grouped = groupImportedElementAdds(
        version,
        proposalId,
        result.changes,
      );
      if (grouped.needsProposal) {
        enqueueSnackbar(IMPORT_REPLACE_NEEDS_PROPOSAL_MESSAGE, {
          variant: 'warning',
        });
        return;
      }
      const placed = placeRemappedImportAdds(
        version,
        proposalId,
        grouped.changes,
      );
      if (placed.length === 0) return;
      void applyChanges(placed, {
        snackbar: {
          type: 'undo',
          message: bulkUpdatedMessage(result.appliedRowIds),
        },
      });
    },
    [applyChanges, enqueueSnackbar, version],
  );

  const visibleSelectedRows = useCallback((): CandidateRow[] => {
    const visibleRowIds = getVisibleListRowIds();
    return selectedRows.filter((row) => visibleRowIds.has(row.id));
  }, [selectedRows]);

  const shouldFanOut = useCallback(
    (rowId: string) => {
      const visibleRows = visibleSelectedRows();
      return (
        visibleRows.length > 1 && visibleRows.some((row) => row.id === rowId)
      );
    },
    [visibleSelectedRows],
  );

  const setMapping = useCallback(
    (referenceId: string, referenceType: MappingReferenceType) => {
      if (!lookups) return;
      apply(
        buildBulkMappingChanges(
          visibleSelectedRows(),
          lookups,
          referenceId,
          referenceType,
        ),
      );
    },
    [apply, lookups, visibleSelectedRows],
  );

  const setQuantityName = useCallback(
    (name: ElementQuantityExpressionName) => {
      if (!lookups) return;
      apply(buildBulkQuantityNameChanges(visibleSelectedRows(), lookups, name));
    },
    [apply, lookups, visibleSelectedRows],
  );

  const setTotalQuantity = useCallback(
    (value: number, sourceRowId: string) => {
      if (!lookups) return;
      apply(
        buildBulkTotalQuantityChanges(
          visibleSelectedRows(),
          lookups,
          value,
          sourceRowId,
        ),
      );
    },
    [apply, lookups, visibleSelectedRows],
  );

  const setConversionRate = useCallback(
    (rate: {
      quantityFactor: number | undefined;
      unitTo?: ConversionFactorUnit;
    }) => {
      if (!lookups) return;
      apply(buildBulkConversionChanges(visibleSelectedRows(), lookups, rate));
    },
    [apply, lookups, visibleSelectedRows],
  );

  const setIfcMeasure = useCallback(
    (measure: IfcMeasure) => {
      if (!lookups) return;
      apply(
        buildBulkIfcMeasureChanges(visibleSelectedRows(), lookups, measure),
      );
    },
    [apply, lookups, visibleSelectedRows],
  );

  // `bulk` is a prop of the memoized CandidateRowListItem, so a fresh object
  // each render would defeat the memo for every row in the section.
  return useMemo(
    () => ({
      shouldFanOut,
      setMapping,
      setQuantityName,
      setTotalQuantity,
      setConversionRate,
      setIfcMeasure,
    }),
    [
      shouldFanOut,
      setMapping,
      setQuantityName,
      setTotalQuantity,
      setConversionRate,
      setIfcMeasure,
    ],
  );
};
